import "@/App.css";
import { Globe, Cog, Link2, BarChart3, MessageCircle, CheckCircle, ArrowRight, Headphones, Lightbulb, Handshake, Clock, Award, Users, Menu, X, Sparkles, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";

const WHATSAPP_NUMBER = "5535635890";
const WHATSAPP_MESSAGE = "Hola, me interesa conocer más sobre tus servicios de desarrollo de software.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Services data
const services = [
  {
    icon: Globe,
    title: "Páginas web profesionales",
    description: "Rápidas, modernas y listas para atraer clientes. Tu presencia digital que convierte visitantes en ventas."
  },
  {
    icon: Cog,
    title: "Automatización de procesos",
    description: "Elimina tareas manuales y ahorra tiempo a tu equipo. Deja que la tecnología trabaje por ti."
  },
  {
    icon: Link2,
    title: "Integración de sistemas",
    description: "Conecta tus herramientas para que trabajen juntas. Un ecosistema digital sin fricciones."
  },
  {
    icon: BarChart3,
    title: "Sistemas a la medida",
    description: "Software diseñado exactamente para tu operación. Soluciones únicas para problemas únicos."
  },
  {
    icon: Sparkles,
    title: "Inteligencia Artificial",
    description: "Integra IA en tu negocio: chatbots, automatización inteligente, análisis de datos y más."
  }
];

// Why work with me
const whyMe = [
  {
    icon: Award,
    title: "11 años de experiencia",
    description: "Más de una década resolviendo problemas tecnológicos para empresas de todos los tamaños."
  },
  {
    icon: CheckCircle,
    title: "Enfocado en resultados",
    description: "No solo entrego código, entrego soluciones que generan valor real para tu negocio."
  },
  {
    icon: Users,
    title: "Comunicación clara",
    description: "Sin tecnicismos. Te explico todo en un lenguaje que entiendas y con el que te sientas cómodo."
  }
];

// How I work
const howIWork = [
  {
    step: "01",
    title: "Escucho tu problema",
    description: "Entiendo a fondo tu situación, tus necesidades y tus objetivos de negocio.",
    icon: Headphones
  },
  {
    step: "02",
    title: "Propongo la solución",
    description: "Diseño una estrategia clara con tiempos, costos y resultados esperados.",
    icon: Lightbulb
  },
  {
    step: "03",
    title: "Lo entregamos juntos",
    description: "Trabajamos en equipo hasta que tu solución esté funcionando perfectamente.",
    icon: Handshake
  }
];

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-gray-900/95 backdrop-blur-xl shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <a href="/" data-testid="logo" className="flex items-center gap-2">
            <span className={`font-semibold text-lg tracking-tight ${scrolled ? "text-white" : "text-gray-900"}`}>BaimerAi</span>
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#servicios" className={`text-sm transition-colors ${scrolled ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}>Servicios</a>
            <a href="#proceso" className={`text-sm transition-colors ${scrolled ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}>Proceso</a>
            <a href="#sobre-mi" className={`text-sm transition-colors ${scrolled ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}>Sobre mí</a>
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
              scrolled ? "bg-white text-gray-900 hover:bg-gray-100" : "bg-gray-900 text-white hover:bg-gray-800"
            }`}
          >
            Contáctame
          </a>

          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col gap-4">
              <a href="#servicios" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Servicios</a>
              <a href="#proceso" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Proceso</a>
              <a href="#sobre-mi" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Sobre mí</a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-5 py-3 text-sm font-medium text-white"
              >
                Contáctame
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - Tesla Style with Images
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center bg-white pt-16"
    >
      {/* Background subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <p className="text-sm font-medium text-gray-500 mb-4 tracking-wide animate-fade-in-up">
              Soluciones Informáticas BaimerAi
            </p>
            
            <h1 
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 mb-6 tracking-tight leading-tight animate-fade-in-up animation-delay-100"
            >
              Soluciones tecnológicas a la medida de tu negocio
            </h1>
            
            <p 
              data-testid="hero-subtitle"
              className="text-lg sm:text-xl text-gray-500 mb-10 max-w-xl animate-fade-in-up animation-delay-200"
            >
              Desarrollo sistemas, automatizo procesos e integro tecnología con inteligencia artificial para que tu empresa funcione mejor.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-8 py-4 text-sm font-medium text-white transition-all hover:bg-gray-800"
              >
                Hablemos de tu proyecto
              </a>
              <a
                href="#servicios"
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-100 px-8 py-4 text-sm font-medium text-gray-900 transition-all hover:bg-gray-200"
              >
                Ver servicios
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Hero Images Grid */}
          <div className="hidden lg:block animate-fade-in-up animation-delay-200">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&auto=format&fit=crop&q=80"
                alt="Desarrollo de software"
                className="w-full h-48 object-cover rounded-xl"
              />
              <img
                src="https://images.unsplash.com/photo-1590524542582-b79728e077dd?w=400&auto=format&fit=crop&q=80"
                alt="iMac profesional"
                className="w-full h-48 object-cover rounded-xl"
              />
              <img
                src="https://images.unsplash.com/photo-1765648580808-76d75e4f3833?w=400&auto=format&fit=crop&q=80"
                alt="Pequeño negocio"
                className="w-full h-48 object-cover rounded-xl"
              />
              <img
                src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=400&auto=format&fit=crop&q=80"
                alt="Reunión corporativa"
                className="w-full h-48 object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-gray-300 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-gray-400 rounded-full animate-scroll-down" />
        </div>
      </div>
    </section>
  );
};

// Services Section - Tesla Style
const ServicesSection = () => {
  return (
    <section id="servicios" data-testid="services-section" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
            Servicios
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto">
            Todo lo que necesitas para digitalizar tu negocio
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className="group relative bg-gray-50 rounded-2xl p-8 transition-all duration-300 hover:bg-gray-100"
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                  <service.icon className="w-6 h-6 text-gray-700" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-500 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Me Section - Tesla Style
const WhyMeSection = () => {
  return (
    <section data-testid="why-me-section" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
            Por qué trabajar conmigo
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {whyMe.map((item, index) => (
            <div 
              key={index}
              data-testid={`why-me-card-${index}`}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-6 shadow-sm">
                <item.icon className="w-7 h-7 text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-500 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How I Work Section - Tesla Style
const HowIWorkSection = () => {
  return (
    <section id="proceso" data-testid="how-i-work-section" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
            Cómo trabajo
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {howIWork.map((item, index) => (
            <div 
              key={index}
              data-testid={`how-i-work-step-${index}`}
              className="text-center"
            >
              <div className="relative inline-flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-gray-700" />
                </div>
                <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gray-900 flex items-center justify-center text-xs font-semibold text-white">
                  {index + 1}
                </span>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {item.title}
              </h3>
              
              <p className="text-gray-500 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section - Expanded Tesla Style
const AboutSection = () => {
  const stats = [
    { number: "+11", label: "Años de experiencia" },
    { number: "+50", label: "Proyectos entregados" },
    { number: "100%", label: "Compromiso" }
  ];

  return (
    <section id="sobre-mi" data-testid="about-section" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* Main About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <div className="order-2 lg:order-1">
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight mb-6">
              Sobre mí
            </h2>
            <p className="text-gray-500 leading-relaxed mb-6">
              Con más de 11 años de experiencia en el desarrollo de software, he ayudado a decenas de empresas a transformar sus operaciones a través de la tecnología. Mi enfoque siempre ha sido el mismo: entender primero el problema de negocio, y después encontrar la solución tecnológica más efectiva.
            </p>
            <p className="text-gray-500 leading-relaxed mb-6">
              No me interesa vender tecnología por vender. Me interesa que tu inversión genere resultados reales: más ventas, menos costos operativos, o procesos más eficientes. Cada proyecto es único, y por eso cada solución que desarrollo está pensada específicamente para las necesidades de tu negocio.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              Trabajo con las herramientas más modernas del mercado, pero siempre eligiendo la tecnología correcta para cada caso, no la más popular. Mi objetivo es que tu inversión en tecnología sea una decisión inteligente que se traduzca en crecimiento real.
            </p>
            
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-5 h-5 text-gray-500" />
                <span className="font-medium">+11 años de experiencia</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-gray-500" />
                <span className="font-medium">+50 proyectos entregados</span>
              </div>
            </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&auto=format&fit=crop&q=80"
                alt="Desarrollo de software"
                className="w-full h-auto object-cover rounded-2xl"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-3 gap-8 mb-24">
          {stats.map((stat, index) => (
            <div key={index} className="text-center" data-testid={`stat-${index}`}>
              <div className="text-4xl sm:text-5xl font-semibold text-gray-900 mb-2">
                {stat.number}
              </div>
              <div className="text-gray-500 text-sm">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* From Small Business to Enterprise Section */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight mb-4">
              Desde pequeños negocios hasta grandes empresas
            </h3>
            <p className="text-gray-500 max-w-2xl mx-auto">
              No importa el tamaño de tu negocio. Ya sea que tengas una cafetería, una tienda, o una empresa con cientos de empleados, tengo la solución tecnológica adecuada para ti.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative rounded-2xl overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1765648580808-76d75e4f3833?w=800&auto=format&fit=crop&q=80"
                alt="Pequeño negocio - Cafetería"
                className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                data-testid="small-business-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div>
                  <h4 className="text-white text-xl font-semibold mb-1">Pequeños negocios</h4>
                  <p className="text-gray-300 text-sm">Cafeterías, tiendas, freelancers, startups</p>
                </div>
              </div>
            </div>
            
            <div className="relative rounded-2xl overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=800&auto=format&fit=crop&q=80"
                alt="Empresa corporativa"
                className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                data-testid="enterprise-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div>
                  <h4 className="text-white text-xl font-semibold mb-1">Empresas</h4>
                  <p className="text-gray-300 text-sm">Corporativos, equipos grandes, operaciones complejas</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy Section with Images */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1566923121432-10c888f55bd0?w=400&auto=format&fit=crop&q=80"
                alt="Setup Apple profesional"
                className="w-full h-48 object-cover rounded-xl"
                data-testid="about-image-2"
              />
              <img
                src="https://images.unsplash.com/photo-1537498425277-c283d32ef9db?w=400&auto=format&fit=crop&q=80"
                alt="Windows laptop"
                className="w-full h-48 object-cover rounded-xl"
                data-testid="about-image-3"
              />
            </div>
            <img
              src="https://images.unsplash.com/photo-1658086351750-1ec43149a713?w=800&auto=format&fit=crop&q=80"
              alt="Estación de trabajo profesional"
              className="w-full h-64 object-cover rounded-xl mt-4"
              data-testid="about-image-4"
            />
          </div>
          
          <div>
            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight mb-6">
              Mi filosofía de trabajo
            </h3>
            <p className="text-gray-500 leading-relaxed mb-6">
              Creo firmemente que la tecnología debe ser un facilitador, no un obstáculo. Por eso, cada solución que desarrollo está pensada para ser intuitiva, escalable y fácil de mantener.
            </p>
            <p className="text-gray-500 leading-relaxed mb-6">
              Trabajo de manera transparente, con comunicación constante y entregas incrementales. Así puedes ver el progreso de tu proyecto en tiempo real y hacer ajustes cuando sea necesario.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-600">Código limpio y documentado para fácil mantenimiento</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-600">Entregas semanales con demos funcionales</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-600">Soporte post-lanzamiento incluido</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-600">Capacitación para tu equipo</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section - Tesla Style
const FinalCTASection = () => {
  return (
    <section data-testid="final-cta-section" className="py-32 bg-gray-900">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">
          ¿Listo para transformar tu negocio?
        </h2>
        <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
          Platiquemos sobre tu proyecto. Sin compromiso, sin presión.
        </p>
        
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="final-cta-whatsapp-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-10 py-4 text-sm font-medium text-gray-900 transition-all hover:bg-gray-100"
        >
          <MessageCircle className="w-5 h-5" />
          Iniciar conversación
        </a>
      </div>
    </section>
  );
};

// Footer - Tesla Style
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 bg-gray-900 border-t border-gray-800">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Soluciones Informáticas BaimerAI
          </span>
          <div className="flex items-center gap-6">
            <Link 
              to="/aviso-privacidad" 
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
              data-testid="privacy-link"
            >
              Aviso de Privacidad
            </Link>
            <Link 
              to="/terminos-condiciones" 
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
              data-testid="terms-link"
            >
              Términos y Condiciones
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button - Clean Style
const FloatingWhatsApp = () => {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp-btn"
      className="fixed bottom-8 right-8 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-green-600 hover:shadow-xl"
      aria-label="Contactar por WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
};

// Legal Page Header
const LegalHeader = () => {
  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </Link>
          <span className="font-semibold text-gray-900">BaimerAI</span>
        </div>
      </div>
    </header>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader />
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4" data-testid="privacy-title">
          Aviso de Privacidad
        </h1>
        <p className="text-gray-500 mb-12">Última actualización: Diciembre 2025</p>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 leading-relaxed mb-8">
            En BaimerAI nos comprometemos a proteger la privacidad de nuestros usuarios y clientes.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">1. Datos que recopilamos</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            No recopilamos datos personales directamente a través de este sitio web.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            Sin embargo, si decides contactarnos mediante WhatsApp, podremos recibir datos como:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Nombre</li>
            <li>Número telefónico</li>
            <li>Información relacionada con tu solicitud o proyecto</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">2. Finalidad del uso de datos</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Los datos proporcionados serán utilizados únicamente para:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Responder consultas</li>
            <li>Proporcionar información sobre nuestros servicios</li>
            <li>Elaborar cotizaciones</li>
            <li>Dar seguimiento a proyectos</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">3. Protección de la información</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Nos comprometemos a proteger tu información y no compartirla con terceros sin tu consentimiento, salvo que sea requerido por ley.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">4. Uso de servicios de terceros</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            La comunicación se realiza a través de WhatsApp, propiedad de Meta Platforms, por lo que el tratamiento de datos también está sujeto a sus políticas de privacidad.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">5. Derechos ARCO</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Puedes solicitar en cualquier momento el acceso, rectificación o eliminación de tus datos enviando un mensaje al mismo canal de contacto.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">6. Cambios al aviso de privacidad</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Nos reservamos el derecho de modificar este aviso en cualquier momento. Los cambios serán publicados en esta misma página.
          </p>

          <p className="text-gray-600 leading-relaxed mt-10 p-4 bg-gray-50 rounded-lg">
            Si tienes dudas sobre este aviso, puedes contactarnos vía WhatsApp.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Terms and Conditions Page
const TermsConditionsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader />
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4" data-testid="terms-title">
          Términos y Condiciones
        </h1>
        <p className="text-gray-500 mb-12">Última actualización: Diciembre 2025</p>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 leading-relaxed mb-8">
            Al acceder a este sitio web, aceptas los siguientes términos:
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">1. Servicios</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            BaimerAI ofrece servicios de desarrollo de software, automatización y soluciones basadas en inteligencia artificial.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">2. Uso del sitio</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            El contenido de este sitio es informativo y no constituye una oferta contractual formal.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">3. Contacto</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            El contacto se realiza a través de WhatsApp. Cualquier acuerdo comercial se definirá posteriormente de manera formal entre ambas partes.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">4. Limitación de responsabilidad</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            No garantizamos que los servicios estén libres de errores o interrupciones, aunque nos comprometemos a ofrecer soluciones de alta calidad.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            No somos responsables por:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Pérdidas indirectas</li>
            <li>Decisiones tomadas con base en información proporcionada por sistemas automatizados o IA</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">5. Propiedad intelectual</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Todo el contenido del sitio (textos, diseño, estructura) pertenece a BaimerAI y no puede ser utilizado sin autorización.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">6. Modificaciones</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Nos reservamos el derecho de actualizar estos términos en cualquier momento.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <WhyMeSection />
      <HowIWorkSection />
      <AboutSection />
      <FinalCTASection />
      <Footer />
      <FloatingWhatsApp />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <div className="App bg-white min-h-screen">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/aviso-privacidad" element={<PrivacyPolicyPage />} />
          <Route path="/terminos-condiciones" element={<TermsConditionsPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
